import React, { useState, useRef, useCallback, useContext, useEffect, createContext } from 'react';
import ReactDOM from 'react-dom';

interface IDrawerStackContext {
  depth: number;
  setChildActive: (active: boolean) => void;
  setChildHeight: (height: number) => void;
}

const DrawerStackContext = createContext<IDrawerStackContext>({
  depth: 0,
  setChildActive: () => {},
  setChildHeight: () => {},
});

interface IMobileBottomDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string | React.ReactNode | null;
  stickyTitle?: boolean;
  maxHeight?: string;
  className?: string;
  contentClassName?: string;
}

const DISMISS_THRESHOLD = 50;

export const MobileBottomDrawer = (props: IMobileBottomDrawerProps) => {
  const { maxHeight = '85dvh' } = props;
  const [isClosing, setIsClosing] = useState(false);
  const [isDragDismissing, setIsDragDismissing] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [childIsActive, setChildIsActive] = useState(false);
  const [childHeight, setChildHeight] = useState(0);
  const [ownHeight, setOwnHeight] = useState<number | null>(null);
  const dragStartY = useRef(0);
  const isDragging = useRef(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const { depth, setChildActive, setChildHeight: reportHeightToParent } = useContext(DrawerStackContext);

  // Signal parent when this drawer opens/closes, and report our height
  useEffect(() => {
    if (props.isOpen) {
      setChildActive(true);
    }
    return () => {
      setChildActive(false);
      reportHeightToParent(0);
    };
  }, [props.isOpen, setChildActive, reportHeightToParent]);

  // Snapshot natural height when child opens, clear explicit height after child closes
  useEffect(() => {
    if (childIsActive && panelRef.current) {
      setOwnHeight(panelRef.current.offsetHeight);
    }
    if (!childIsActive && ownHeight !== null) {
      const timer = setTimeout(() => setOwnHeight(null), 300);
      return () => clearTimeout(timer);
    }
  }, [childIsActive]);

  // Measure and report own height to parent via callback ref
  const measuredRef = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node;
      if (node && props.isOpen) {
        // Double-rAF ensures the browser has completed layout + paint
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            reportHeightToParent(node.offsetHeight);
          });
        });
      }
    },
    [props.isOpen, reportHeightToParent]
  );

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setDragOffset(0);
      props.onClose();
    }, 140);
  }, [props.onClose]);

  // Escape key handler (replaces focus-trap's built-in Escape handling)
  useEffect(() => {
    if (!props.isOpen || childIsActive) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [props.isOpen, childIsActive, handleClose]);

  const handleDragDismiss = useCallback(() => {
    setIsDragDismissing(true);
    setDragOffset(window.innerHeight);
    setTimeout(() => {
      setIsDragDismissing(false);
      setIsClosing(false);
      setDragOffset(0);
      props.onClose();
    }, 140);
  }, [props.onClose]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't dismiss parent when child is active
      if (childIsActive) return;
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose, childIsActive]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      // Don't allow drag on parent when child is active
      if (childIsActive) return;
      dragStartY.current = e.touches[0].clientY;
      isDragging.current = true;
      setDragOffset(0);
    },
    [childIsActive]
  );

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const currentY = e.touches[0].clientY;
    const delta = currentY - dragStartY.current;
    if (delta > 0) {
      setDragOffset(delta);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    isDragging.current = false;
    if (dragOffset > DISMISS_THRESHOLD) {
      handleDragDismiss();
    } else {
      setDragOffset(0);
    }
  }, [dragOffset, handleDragDismiss]);

  if (!props.isOpen && !isClosing && !isDragDismissing) return null;

  const overlayZ = 100000 + depth * 10;
  const panelZ = 100001 + depth * 10;

  const panelStyle: React.CSSProperties = {
    transform: dragOffset > 0 ? `translateY(${dragOffset}px)` : undefined,
    transition: isDragging.current
      ? 'none'
      : 'transform 0.15s ease-in, height 0.3s ease',
    ...(childIsActive && childHeight > 0
      ? { height: `${childHeight + 80}px` }
      : ownHeight !== null
      ? { height: `${ownHeight}px` }
      : {}),
  };

  const panelAnimClass = isDragDismissing ? '' : isClosing ? 'animate-slide-down' : 'animate-slide-up';

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 bg-black/40 ${
          isClosing || isDragDismissing ? 'animate-fade-out' : 'animate-fade-in'
        }`}
        style={{ touchAction: 'none', zIndex: overlayZ }}
        onClick={handleOverlayClick}
      />
        <div
          ref={measuredRef}
          tabIndex={-1}
          className={`bg-white fixed bottom-0 left-0 right-0 rounded-t-[10px] outline-none overflow-hidden w-[100vw] ${panelAnimClass} ${props.className ??
            ''}`}
          style={{ ...panelStyle, zIndex: panelZ }}
        >
          {/* Drag handle + sticky title area */}
          <div
            className={props.stickyTitle && props.title ? 'sticky top-0 bg-white z-[1]' : ''}
          >
            <div
              className="flex justify-center items-center pt-[12px] pb-[8px] cursor-grab active:cursor-grabbing"
              style={{ touchAction: 'none' }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="w-[36px] h-[5px] bg-gray-20 rounded-full" />
            </div>
            {props.title != null && (
              <div className="px-[20px] pb-[8px]">
                {typeof props.title === 'string' ? (
                  <span className="block font-hurmegeometric-sans text-sm uppercase text-flint font-bold">
                    {props.title}
                  </span>
                ) : (
                  props.title
                )}
              </div>
            )}
          </div>

          {/* Scrollable content */}
          <div
            className={`px-[20px] pb-[20px] overflow-x-hidden overflow-y-auto ${props.contentClassName ?? ''}`}
            style={{ maxHeight, overscrollBehavior: 'contain' }}
          >
            <DrawerStackContext.Provider value={{ depth: depth + 1, setChildActive: setChildIsActive, setChildHeight: setChildHeight }}>
              {props.children}
            </DrawerStackContext.Provider>
          </div>
        </div>
    </>,
    document.body
  );
};
